import React, { useEffect, Fragment, useState } from 'react'
import {
  Col,
  Form,
  LockIcon,
  SecondaryLink,
  color,
  spacer,
  ModalV2,
  Dialog,
  Lock,
} from 'stylist'
import yup from 'yup'
import styled from '@emotion/styled'
import { css } from '@emotion/core'
import SSOProviderButtons from 'components/SSOProviderButtons'
import FormLinks from 'components/FormLinks'
import { findPlatform } from 'config/platforms'
import { yupToFormErrors } from 'formik'
import { isFunction } from 'utils'
import { getSSOForEmailInput, getUriForEmailInput } from 'utils/sso'
import { verifyValid2FA } from 'utils/api'
import TwoFactorSetup from './TwoFactorSetup'
import TwoFactorCode from './TwoFactorCode'

const StyledFormLinks = styled(FormLinks)`
  justify-content: left !important;
  margin-top: ${spacer(1)};
`

const GreyedFormGroup = styled(Form.Group)`
  ${({ cookiesAccepted, matchedSsoProvider }) =>
    cookiesAccepted &&
    matchedSsoProvider &&
    css`
      opacity: 0.5;
      filter: grayscale(100%);
      pointer-events: none;
    `};
`

const SSOFormGroup = styled(Form.Group)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  color: ${color('cadet')};

  span {
    margin-left: ${spacer(2)};
  }
`

const ActionFormGroup = styled(Form.Group)`
  > div {
    margin-top: 0;
  }
`

/**
 * For the 2FA modal
 */

const modalRoot = document.createElement('div')
modalRoot.setAttribute('id', 'modal-root')
document.body.appendChild(modalRoot)

const ModalButton = ({ show, ...rest }) => (
  <div
    onClick={() => show()}
    {...rest}
    style={{ color: '#a3cf62', display: 'none' }}
    id="trigger-modal"
  >
    <i className="fa fa-pencil" /> <span>Edit</span>
  </div>
)

const StyledModalButton = styled(ModalButton)`
  cursor: pointer;
  margin-top: ${spacer(2)};
  padding: 0;
  display: block;
  white-space: nowrap;
`

const ModalDialog = styled(Dialog)`
  width: 50%;
  max-height: 800px;
  padding: 20px;

  ${({ firstTime2FA }) =>
    firstTime2FA &&
    `
    @media only screen and (max-width: 1000px) {
      max-height: 1500px;
      margin: auto;
    }
  `}

  ${({ firstTime2FA }) =>
    !firstTime2FA &&
    `
    width: 35% !important;
    @media only screen and (max-width: 900px) {
      width: 55% !important;
    }
  `}

  @media only screen and (max-width: 790px) {
    width: 100% !important;
  }
`
const ModalTitle = styled(Dialog.Title)`
  text-overflow: ellipsis;
  overflow: hidden;
  border-bottom: 1px solid #f0eceb;
  ${({ firstTime2FA }) =>
    !firstTime2FA &&
    `
    font-size: 1.2rem !important;
  `}

  @media only screen and (max-width: 1000px) {
    font-size: 0.8rem !important;
  }
`

const cookieError = 'Cookie policy must be accepted'
const schema = yup.object().shape({
  email: yup
    .string()
    .ensure()
    .email('Email must be a valid email address')
    .required("Email can't be blank"),
  password: yup.string().required("Password can't be blank"),
  cookiesAccepted: yup
    .boolean()
    .test('is-accepted', cookieError, (value) => value),
})

const LoginForm = ({
  query,
  notify,
  matchedSsoProvider,
  setState,
  authenticate,
  cookiesAccepted,
  highlightAcceptCookiesButton,
  uri2FA,
  twoFAEnabled,
  code,
  history,
  path,
}) => {
  const platform = findPlatform(query.get('client_id')) || {}
  const [firstTime2FA, setFirstTime2FA] = useState(false)
  let mounted = true

  let signupUrl = ''

  // When a user signs up for Video Streaming product persist the state in the redirect_url param
  if (platform.id === 'VideoStreaming') {
    const redirectUriFromUrl = query.get('redirect_uri') || '';
    const stateFromUrl = query.get('state') || '';
    const fullRedirectUri = new URL(redirectUriFromUrl)
    fullRedirectUri.searchParams.set('state', stateFromUrl)
    signupUrl = `${platform.signupUrl}&redirect_uri=${encodeURIComponent(fullRedirectUri.toString())}`
  } else {
    signupUrl = isFunction(platform.signupUrl) && platform.ssoSignUp
                    ? platform.signupUrl(window.location.search)
                    : platform.signupUrl
  }


  useEffect(() => {
    if (query.get('email')) {
      check2FASecret(query.get('email'))
    }

    return () => {
      mounted = false
    }
  }, [])

  const checkEmail = (value) => {
    checkSSOMatch(value)
    check2FASecret(value)
  }

  const checkSSOMatch = (value) =>
    getSSOForEmailInput(
      value,
      (match) => mounted && setState({ matchedSsoProvider: match })
    )
  const check2FASecret = (value) => {
    getUriForEmailInput(
      value,
      (responseBody) =>
        mounted && setState({ twoFAEnabled: responseBody.two_fa_enabled }),
      () => mounted && setState({ twoFAEnabled: false })
    )
  }

  const twoFaLogin = (email, password, notify) => {
    verifyValid2FA(email, password)
      .then((res) => {
        const { uri, code, first_time_2fa } = res
        setState({ uri2FA: uri, code: code })
        setFirstTime2FA(first_time_2fa)

        document.querySelector('#trigger-modal').click()
      })
      .catch(({ error }) => {
        if (error === 'invalid_grant') {
          notify('warning', 'Email or password is incorrect')
        } else if (error === 'unconfirmed_user') {
          notify('info', 'Account confirmation required')
          history.push(`${path}/confirmation`)
        } else if (error === 'password_expired') {
          notify('info', 'Your password is expired, please reset.')
          history.push(
            `${path}/reset-password?reset_password_token=${encodeURIComponent(
              error.reset_password_token
            )}&email=${encodeURIComponent(error.email)}`
          )
        }
        setState({ uri2FA: '', code: '' })
      })
  }

  const ModalTwoFA = () => (
    <ModalV2
      lazyRender={true}
      render={({ hide }) => (
        <ModalDialog firstTime2FA={firstTime2FA}>
          <Dialog.Header onClose={() => hide()}>
            <ModalTitle firstTime2FA={firstTime2FA}>
              <Lock size="xl" fill="blue" /> Two Factor Authentication Setup
            </ModalTitle>
          </Dialog.Header>
          {firstTime2FA ? (
            <TwoFactorSetup
              uri2FA={uri2FA}
              code={code}
              notify={notify}
              authenticate={authenticate}
              product={query.get('client_id') || 'Forsta'}
            />
          ) : (
            <TwoFactorCode notify={notify} authenticate={authenticate} />
          )}
        </ModalDialog>
      )}
    >
      {({ show }) => <StyledModalButton show={show} />}
    </ModalV2>
  )

  return (
    <Fragment>
      <ModalTwoFA />
      <Form
        initialValues={{
          email: query.get('email') || '',
          password: '',
        }}
        validate={(values) => {
          const allValues = { ...values, cookiesAccepted }

          return schema
            .validate(allValues, { abortEarly: false })
            .then(() => {
              return {}
            })
            .catch((error) => {
              error.errors.find((a) => a === cookieError) &&
                highlightAcceptCookiesButton(true)
              throw yupToFormErrors(error)
            })
        }}
        onSubmit={({ email, password }) => {
          if (!twoFAEnabled) {
            authenticate('oauth2-password-grant', {
              email,
              password,
            }).catch(({ payload: { error } }) => {
              if (error === 'invalid_grant') {
                notify('warning', 'Email or password is incorrect')
              } else if (error === 'unconfirmed_user') {
                notify('info', 'Account confirmation required')
              } else if (error === 'password_expired') {
                notify('info', 'Your password is expired, please reset.')
              }
            })
          } else {
            twoFaLogin(email, password, notify)
          }
        }}
      >
        <Col>
          <Form.Errors />
          <Form.Group>
            <label htmlFor="email">Email</label>
            <Form.Input
              id="email"
              type="email"
              name="email"
              autoComplete="off"
              onBlur={() =>
                setState({
                  redirectToSsoProvider: Boolean(matchedSsoProvider),
                })
              }
              onChange={(e) => checkEmail(e.target.value)}
            />
          </Form.Group>
          {matchedSsoProvider ? (
            <SSOFormGroup>
              <LockIcon size=".8rem" />
              <span>Single sign-on enabled</span>
            </SSOFormGroup>
          ) : (
            <Form.Group>
              <label htmlFor="password">Password</label>
              <Form.Input
                id="password"
                type="password"
                name="password"
                autoComplete="off"
              />
              <StyledFormLinks>
                <SecondaryLink to="/login/forgot-password">
                  Forgot Password
                </SecondaryLink>
              </StyledFormLinks>
            </Form.Group>
          )}
          {twoFAEnabled ? (
            <ActionFormGroup>
              <Form.SubmitButton block={true}>
                Two-Factor Authentication
              </Form.SubmitButton>
            </ActionFormGroup>
          ) : (
            <Fragment>
              <ActionFormGroup>
                <Form.Errors name="cookiesAccepted" />
                <Form.SubmitButton block={true}>
                  {matchedSsoProvider ? 'Continue' : 'Sign In'}
                </Form.SubmitButton>
              </ActionFormGroup>
              <GreyedFormGroup
                matchedSsoProvider={matchedSsoProvider}
                cookiesAccepted={cookiesAccepted}
              >
                <SSOProviderButtons
                  cookiesAccepted={cookiesAccepted}
                  cookieError={cookieError}
                />
              </GreyedFormGroup>
            </Fragment>
          )}
          {platform.signupUrl && (
            <div>
              Don't have an account?{' '}
              <SecondaryLink
                to={signupUrl}
              >
                Sign Up
              </SecondaryLink>
            </div>
          )}
        </Col>
      </Form>
    </Fragment>
  )
}

export default LoginForm



// WEBPACK FOOTER //
// ./src/routes/login/components/LoginForm.js